import { useTenantSettings, OtherIncomeTypes } from "@nestoca/multi-tenant";
import { useTranslation } from "next-i18next";

export type OtherIncome = typeof OtherIncomeTypes;

export type OtherIncomeValue = OtherIncome[keyof OtherIncome];

type OtherIncomeMap = Record<
  OtherIncomeValue,
  {
    id: OtherIncomeValue;
    labelKey: string;
  }
>;

export const OTHER_INCOME_MAP: OtherIncomeMap = {
  [OtherIncomeTypes.INVESTMENT_INCOME]: {
    id: OtherIncomeTypes.INVESTMENT_INCOME,
    labelKey: "form:otherIncomeType.investment",
  },
  [OtherIncomeTypes.INTEREST_INCOME]: {
    id: OtherIncomeTypes.INTEREST_INCOME,
    labelKey: "form:otherIncomeType.interest",
  },
  [OtherIncomeTypes.ALIMONY]: {
    id: OtherIncomeTypes.ALIMONY,
    labelKey: "form:otherIncomeType.alimony",
  },
  [OtherIncomeTypes.CHILD_SUPPORT]: {
    id: OtherIncomeTypes.CHILD_SUPPORT,
    labelKey: "form:otherIncomeType.childSupport",
  },
  [OtherIncomeTypes.EMPLOYMENT_EXPENSE]: {
    id: OtherIncomeTypes.EMPLOYMENT_EXPENSE,
    labelKey: "form:otherIncomeType.employment",
  },
  [OtherIncomeTypes.DISABILITY_INCOME]: {
    id: OtherIncomeTypes.DISABILITY_INCOME,
    labelKey: "form:otherIncomeType.disability",
  },
  [OtherIncomeTypes.PENSION]: {
    id: OtherIncomeTypes.PENSION,
    labelKey: "form:otherIncomeType.pension",
  },
  [OtherIncomeTypes.CHILD_CARE_BENEFIT]: {
    id: OtherIncomeTypes.CHILD_CARE_BENEFIT,
    labelKey: "form:otherIncomeType.childCareBenefit",
  },
  [OtherIncomeTypes.FOSTER_CARE_INCOME]: {
    id: OtherIncomeTypes.FOSTER_CARE_INCOME,
    labelKey: "form:otherIncomeType.fosterCareIncome",
  },
  [OtherIncomeTypes.EI_INDEMNITY_INCOME]: {
    id: OtherIncomeTypes.EI_INDEMNITY_INCOME,
    labelKey: "form:otherIncomeType.eiIndemnityIncome",
  },
  [OtherIncomeTypes.RRIF]: {
    id: OtherIncomeTypes.RRIF,
    labelKey: "form:otherIncomeType.rrif",
  },
  [OtherIncomeTypes.TRUST_INCOME]: {
    id: OtherIncomeTypes.TRUST_INCOME,
    labelKey: "form:otherIncomeType.trustIncome",
  },
  [OtherIncomeTypes.SHORT_TERM_DISABILITY]: {
    id: OtherIncomeTypes.SHORT_TERM_DISABILITY,
    labelKey: "form:otherIncomeType.shortTermDisability",
  },
  [OtherIncomeTypes.RENTAL_INCOME_SURPLUS]: {
    id: OtherIncomeTypes.RENTAL_INCOME_SURPLUS,
    labelKey: "form:otherIncomeType.rentalIncomeSurplus",
  },
  [OtherIncomeTypes.ALIMONY_CHILD_SUPPORT]: {
    id: OtherIncomeTypes.ALIMONY_CHILD_SUPPORT,
    labelKey: "form:otherIncomeType.alimonyChildSupport",
  },
  [OtherIncomeTypes.ANNUITY_INCOME]: {
    id: OtherIncomeTypes.ANNUITY_INCOME,
    labelKey: "form:otherIncomeType.annuityIncome",
  },
  [OtherIncomeTypes.INDIGENOUS_INCOME]: {
    id: OtherIncomeTypes.INDIGENOUS_INCOME,
    labelKey: "form:otherIncomeType.indigenousIncome",
  },
  [OtherIncomeTypes.WORKERS_COMPENSATION]: {
    id: OtherIncomeTypes.WORKERS_COMPENSATION,
    labelKey: "form:otherIncomeType.workersCompensation",
  },
  [OtherIncomeTypes.OTHER]: {
    id: OtherIncomeTypes.OTHER,
    labelKey: "form:otherIncomeType.other",
  },
};

export const useOtherIncomeOptions = (): {
  value: OtherIncomeValue;
  label: string;
}[] => {
  const { t } = useTranslation();

  const {
    typesOverride: {
      settings: { otherIncomeTypes },
    },
  } = useTenantSettings();

  return otherIncomeTypes
    .filter((typeId) => !!OTHER_INCOME_MAP[typeId])
    .map((typeId) => ({
      value: typeId,
      label: t(OTHER_INCOME_MAP[typeId]?.labelKey || ""),
    }));
};
